/**
 * T-054 — Ops contracts (14 §1 · tasks/W4-CONTRACTS.md §7)
 * health · feature flags · context providers · triage · telegram link flow
 * ค่าตายตัว: flags KV cache 60 วิ (key `ff:<key>`) · context cache 5 นาที (KV) / timeout ต่อ provider · telegram nonce 10 นาที
 */
import { z } from "zod";
import { OutboxRecoveryCounts, OutboxRecoveryOverview } from "./email.ts";

// ---------------------------------------------------------------------------
// Health (GET /api/v1/admin/health · POST …/health/run/:task)
// ---------------------------------------------------------------------------

export const HealthReport = z.object({
  db_ms: z.number().nonnegative(),
  queues: z.object({
    inbound_backlog: z.number().int().nonnegative(),
    inbound_processing: z.number().int().nonnegative().default(0),
    inbound_failed: z.number().int().nonnegative().default(0),
    outbox_queued: z.number().int().nonnegative(),
    outbox_failed_24h: z.number().int().nonnegative(),
    domain_events_unpublished: z.number().int().nonnegative(),
  }),
  cron: z.array(
    z.object({
      task: z.string(),
      cron: z.string(),
      last_ok_at: z.iso.datetime().nullable(),
      last_status: z.string().nullable(),
      failures_24h: z.number().int().nonnegative(),
    }),
  ),
  ai_spend_month: z.number().nonnegative(),
  r2: z.null(),
  version: z.string().nullable(),
  // [T-344 B2 · F-DA2-2] additive — สำเนา Sent ที่ค้าง queued โดยไม่ผูก outbox (sweeper `sweepOrphanSent` กินชุดเดียวกัน)
  mail_orphan_sent: z
    .object({
      count: z.number().int().nonnegative(),
      oldest_seconds: z.number().nonnegative().nullable(),
    })
    .optional(),
  // Active send cycles past their lease, including legacy claimed rows without a lease.
  mail_send_cycles_stuck: z
    .object({
      count: z.number().int().nonnegative(),
      oldest_seconds: z.number().nonnegative().nullable(),
    })
    .optional(),
  // [SA-04] additive — ความหยุดของ field เดิมทั้งหมดคงเดิม · categories may overlap (not a summable total)
  recovery: z
    .object({
      counts: OutboxRecoveryCounts,
      oldest_seconds: OutboxRecoveryOverview.shape.oldest_seconds,
    })
    .optional(),
});
export type HealthReport = z.infer<typeof HealthReport>;

export const HealthRunResult = z.object({
  ok: z.boolean(),
  task: z.string(),
  cron: z.string(),
  ok_tasks: z.array(z.string()),
  failed: z.array(z.object({ task: z.string(), error: z.string() })),
  not_acquired: z.array(z.string()),
});
export type HealthRunResult = z.infer<typeof HealthRunResult>;

// ---------------------------------------------------------------------------
// Feature flags (GET/PUT /api/v1/admin/feature-flags/:key · isEnabled)
// rules: users ⊃ userId → true · teams ∩ teamIds → true · percent = hash(userId)%100 < percent · ไม่มี rules → enabled
// ---------------------------------------------------------------------------

export const FeatureFlagKey = z
  .string()
  .regex(/^[a-z][a-z0-9_.-]{1,63}$/, "key ต้องเป็นตัวอักษรพิมพ์เล็ก/ตัวเลข/._- ยาว 2–64 ขึ้นต้นด้วยตัวอักษร");
export type FeatureFlagKey = z.infer<typeof FeatureFlagKey>;

export const FeatureFlagRules = z.strictObject({
  teams: z.array(z.uuid()).max(100).optional(),
  users: z.array(z.uuid()).max(100).optional(),
  percent: z.number().int().min(0).max(100).optional(),
});
export type FeatureFlagRules = z.infer<typeof FeatureFlagRules>;

export const FeatureFlag = z.object({
  key: z.string(),
  enabled: z.boolean(),
  rules: FeatureFlagRules,
  description: z.string().nullable(),
  updated_at: z.iso.datetime().nullable(),
});
export type FeatureFlag = z.infer<typeof FeatureFlag>;

export const FeatureFlagInput = z.object({
  enabled: z.boolean(),
  rules: FeatureFlagRules.optional().default({}),
  description: z.string().max(500).nullable().optional(),
});
export type FeatureFlagInput = z.infer<typeof FeatureFlagInput>;

// ---------------------------------------------------------------------------
// Context providers (admin CRUD + GET /api/v1/tickets/:id/context)
// ---------------------------------------------------------------------------

export const ContextProviderInput = z.object({
  name: z.string().trim().min(1).max(120),
  url: z.string().min(1).max(2048),
  /** ตั้ง = เก็บเข้ารหัส (encodeSecret) · null = ล้าง · ไม่ส่ง = คงเดิม (PATCH) */
  secret: z.string().min(1).max(500).nullable().optional(),
  /** ว่าง = ตรงทุก service */
  service_codes: z.array(z.string().trim().min(1).max(32)).max(50).default([]),
  enabled: z.boolean().default(true),
  timeout_ms: z.number().int().min(100).max(10_000).default(3000),
});
export type ContextProviderInput = z.infer<typeof ContextProviderInput>;

export const ContextProviderPatch = ContextProviderInput.partial();
export type ContextProviderPatch = z.infer<typeof ContextProviderPatch>;

export const ContextProvider = z.object({
  id: z.uuid(),
  name: z.string(),
  url: z.string(),
  has_secret: z.boolean(),
  service_codes: z.array(z.string()),
  enabled: z.boolean(),
  timeout_ms: z.number().int(),
  last_error: z.string().nullable(),
  last_error_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type ContextProvider = z.infer<typeof ContextProvider>;

export const ContextField = z.object({
  label: z.string().max(200),
  value: z.string().max(2000),
  url: z.string().max(2048).nullable().optional(),
});
export type ContextField = z.infer<typeof ContextField>;

export const ProviderContext = z.object({
  provider_id: z.string(),
  provider: z.string(),
  ok: z.boolean(),
  fields: z.array(ContextField),
  error: z.string().nullable(),
});
export type ProviderContext = z.infer<typeof ProviderContext>;

export const TicketContext = z.object({
  ticket_id: z.string(),
  items: z.array(ProviderContext),
  fetched_at: z.iso.datetime(),
  cached: z.boolean(),
});
export type TicketContext = z.infer<typeof TicketContext>;

// ---------------------------------------------------------------------------
// Triage inbox (flag triage.inbox · tag "needs-triage" ของ intake team ที่ user อยู่)
// ---------------------------------------------------------------------------

export const TriageListQuery = z.object({
  team_id: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type TriageListQuery = z.infer<typeof TriageListQuery>;

export const TriageAccept = z.object({
  team_id: z.uuid().optional(),
  expected_version: z.number().int().optional(),
});
export type TriageAccept = z.infer<typeof TriageAccept>;

export const TriageDecline = z.object({
  team_id: z.uuid(),
  note: z.string().max(2000).optional(),
  expected_version: z.number().int().optional(),
});
export type TriageDecline = z.infer<typeof TriageDecline>;

export const TriageDuplicate = z.object({
  of: z.string().min(3).max(24),
  expected_version: z.number().int().optional(),
});
export type TriageDuplicate = z.infer<typeof TriageDuplicate>;

export const TriageAction = z.union([TriageAccept, TriageDecline, TriageDuplicate]);
export type TriageAction = z.infer<typeof TriageAction>;

// ---------------------------------------------------------------------------
// Telegram link flow (POST /api/v1/me/notification-targets/telegram/link + POST /hooks/telegram)
// ---------------------------------------------------------------------------

export const TelegramLink = z.object({
  nonce: z.string().min(8).max(128),
  /** `https://t.me/<bot>?start=<nonce>` — null เมื่อยังไม่ตั้ง bot_username (ส่ง nonce เองได้) */
  link_url: z.string().nullable(),
  expires_in_sec: z.number().int().positive(),
});
export type TelegramLink = z.infer<typeof TelegramLink>;

export const TelegramIntegrationStatus = z.object({
  bot_username: z.string().nullable(),
  has_bot_token: z.boolean(),
  has_webhook_secret: z.boolean(),
});
export type TelegramIntegrationStatus = z.infer<typeof TelegramIntegrationStatus>;

export const TelegramIntegrationInput = z.object({
  bot_username: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_]{4,64}$/, "bot username ต้องเป็น A-Za-z0-9_ ยาว 4–64")
    .nullable()
    .optional(),
  bot_token: z.string().trim().min(10).max(200).nullable().optional(),
  webhook_secret: z.string().trim().min(16).max(200).nullable().optional(),
});
export type TelegramIntegrationInput = z.infer<typeof TelegramIntegrationInput>;
